import {
  Card,
  Chip,
  Link,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { ItemAPI } from '../../api/item';
import { printRoundedNumber } from '../../shared/utils';
import { InventoryManagementDialog } from '../inventory-management/inventory-management-dialog';
import { useNotificationContext } from '../shared/notification';
import {
  CompactTableCell,
  CompactTableRow,
  TableTitle,
} from '../shared/ui-extension';
import { CostDetailDialog } from './cost-detail-dialog';
import { VendorsAPI } from '../../api/vendor-supplier';
import { VendorInventoryManagement } from '../vendor-supplier/vendor-inventory-management';
import PurchaseOrderDetails from '../shared/item/purchase-order-details';

export const ItemWarehouseDetails = ({ itemId, vendorId }) => {
  const { handleException } = useNotificationContext();

  const [inventoryDetails, setInventoryDetails] = useState({});
  const [inventoryDialog, setInventoryDialog] = useState({});
  const [costDetails, setCostDetails] = useState([]);
  const [vendorWarehouseDetails, setVendorWarehouseDetails] = useState([]);

  useEffect(() => {
    const loadInventoryDetails = async (itemId) => {
      try {
        const { data } = await ItemAPI.getItemInventoryDetails(itemId);
        let vendorWarehouseItems = [];
        try {
          const vendorResponse = await VendorsAPI.getVendorWarehouseItems(
            vendorId,
            itemId
          );
          vendorWarehouseItems = vendorResponse.data || [];
        } catch (err) {
          console.error('Failed to fetch vendor warehouse items:', err);
        }

        if (vendorWarehouseItems.length) {
          const cumulativeData = vendorWarehouseItems.reduce(
            (acc, item) => {
              return {
                legacy_location_id: item.location_name,
                location_id: 0,
                qty_avail: acc.qty_avail + item.quantity,
              };
            },
            { qty_avail: 0 }
          );
          data.locations.push(cumulativeData);
          setVendorWarehouseDetails(vendorWarehouseItems);
        }

        setInventoryDetails(data);
      } catch (e) {
        handleException(e);
      }
    };
    if (itemId) {
      loadInventoryDetails(itemId);
    }
  }, [itemId]);

  const { locations } = inventoryDetails;
  const noData = '-';

  if (!locations) {
    return null;
  }

  return (
    <>
      <TableContainer>
        <TableTitle title={`Warehouse Details - ${itemId}`} />
        <Table>
          <TableHead>
            <TableRow>
              <CompactTableCell>Warehouse</CompactTableCell>
              <CompactTableCell>Total QOH</CompactTableCell>
              <CompactTableCell>Full Cases</CompactTableCell>
              <CompactTableCell>Partials</CompactTableCell>
              <CompactTableCell>Avail</CompactTableCell>
              <CompactTableCell>On SO</CompactTableCell>
              <CompactTableCell>On PO</CompactTableCell>
              <CompactTableCell>Transfer In</CompactTableCell>
              <CompactTableCell>Transfer Out</CompactTableCell>
              <CompactTableCell>Standard Cost</CompactTableCell>
              <CompactTableCell>Unit Cost</CompactTableCell>
              <CompactTableCell>Allocated Cost</CompactTableCell>
              <CompactTableCell>Additional Cost</CompactTableCell>
              <CompactTableCell>Total Cost</CompactTableCell>
              <CompactTableCell>Status</CompactTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {locations?.map((l) => {
              const {
                location_id,
                legacy_location_id,
                location_name,
                is_direct_ship,
                qty_avail,
                qty_on_hand,
                perfect_pick_qty_on_hand,
                qty_on_so,
                qty_on_po,
                qty_transfer_in,
                qty_transfer_out,
                standard_cost,
                unit_cost,
                allocated_cost,
                other_cost,
                landed_cost,
                po_cost,
                stockable,
                discountinued,
                sellable,
                fifoLayers,
              } = l;

              const totalCost = is_direct_ship ? po_cost : landed_cost;
              const locationName = legacy_location_id || location_name;
              const stockedByPerfectPick = perfect_pick_qty_on_hand >= 0;

              const tags = [
                stockable === false && { label: 'Non-stock', color: 'primary' },
                discountinued && { label: 'Discontinued', color: 'warning' },
                sellable === false && { label: 'Not Sellable', color: 'error' },
              ].filter((t) => !!t);

              return (
                <CompactTableRow key={legacy_location_id || location_id}>
                  <CompactTableCell>{locationName}</CompactTableCell>
                  <CompactTableCell>
                    {(!is_direct_ship &&
                      location_id &&
                      printRoundedNumber(qty_on_hand, 0)) ||
                      noData}
                  </CompactTableCell>
                  <CompactTableCell>
                    {stockedByPerfectPick ? (
                      <Typography sx={{ fontWeight: 'bold' }}>
                        {printRoundedNumber(
                          qty_on_hand - perfect_pick_qty_on_hand >= 0
                            ? qty_on_hand - perfect_pick_qty_on_hand
                            : 0,
                          0
                        )}
                      </Typography>
                    ) : (
                      noData
                    )}
                  </CompactTableCell>
                  <CompactTableCell>
                    {stockedByPerfectPick ? (
                      <Typography sx={{ fontWeight: 'bold' }}>
                        {printRoundedNumber(perfect_pick_qty_on_hand, 0)}
                      </Typography>
                    ) : (
                      noData
                    )}
                  </CompactTableCell>
                  <CompactTableCell>
                    {(!is_direct_ship && (
                      <Link
                        onClick={() =>
                          setInventoryDialog({
                            isDialogOpen: true,
                            itemId: itemId,
                            locationId: location_id,
                          })
                        }
                      >
                        {printRoundedNumber(qty_avail, 0)}
                      </Link>
                    )) ||
                      noData}
                  </CompactTableCell>
                  <CompactTableCell>
                    {printRoundedNumber(qty_on_so, 0)}
                  </CompactTableCell>
                  <CompactTableCell>
                    <PurchaseOrderDetails
                      quantityOnPo={qty_on_po}
                      legacyLocationId={legacy_location_id}
                      itemId={itemId}
                    />
                  </CompactTableCell>
                  <CompactTableCell>
                    {printRoundedNumber(qty_transfer_in, 0)}
                  </CompactTableCell>
                  <CompactTableCell>
                    {printRoundedNumber(qty_transfer_out, 0)}
                  </CompactTableCell>
                  <CompactTableCell>
                    $ {printRoundedNumber(standard_cost, 5)}
                  </CompactTableCell>
                  <CompactTableCell>
                    $ {printRoundedNumber(unit_cost, 5)}
                  </CompactTableCell>
                  <CompactTableCell>
                    $ {printRoundedNumber(allocated_cost, 5)}
                  </CompactTableCell>
                  <CompactTableCell>
                    $ {printRoundedNumber(other_cost, 5)}
                  </CompactTableCell>
                  <CompactTableCell>
                    {(fifoLayers?.length > 0 && (
                      <Link
                        onClick={() =>
                          setCostDetails({ fifoLayers, locationName })
                        }
                      >
                        $ {printRoundedNumber(totalCost, 5)}
                      </Link>
                    )) ||
                      '$ ' + printRoundedNumber(totalCost, 5)}
                  </CompactTableCell>
                  <CompactTableCell>
                    {tags.map(({ label, color }, i) => (
                      <Chip
                        key={i}
                        label={label}
                        size='small'
                        color={color}
                        sx={{ marginRight: '8px' }}
                      />
                    ))}
                  </CompactTableCell>
                </CompactTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {inventoryDialog.locationId === 0 ? (
        <VendorInventoryManagement
          data={vendorWarehouseDetails}
          isDialogOpen={inventoryDialog.isDialogOpen}
          setOpenDialog={setInventoryDialog}
        />
      ) : (
        <InventoryManagementDialog
          inventoryDialog={inventoryDialog}
          setInventoryDialog={setInventoryDialog}
        />
      )}

      <CostDetailDialog
        costDetails={costDetails}
        setCostDetails={setCostDetails}
      />
    </>
  );
};
